/**
 * Local-development only.
 *
 * In Evo the BFF proxy supplies the ChatOBI API's auth headers and the host
 * supplies the property/group as parameters. Neither exists when running the
 * harness, so the legacy bearer token is obtained the way the CRA app did, from
 * whichever of these is configured in `.env`:
 *
 * - `VITE_CHATOBI_DEV_EMAIL` + `VITE_CHATOBI_DEV_PASSWORD` and one of
 *   `VITE_CHATOBI_DEV_PROPERTY_ID` / `VITE_CHATOBI_DEV_GROUP_ID` (`/internalToken`)
 * - `VITE_CHATOBI_DEV_CODE`, the single-use `random_string` from a
 *   `/verify/<code>` launch link (`/verification`)
 */
import type { RequestHeaders } from './runtime';

export type DevSession = {
  propertyId: string | null;
  groupId: string | null;
  getHeaders: (() => Promise<RequestHeaders>) | null;
};

const env = import.meta.env;

async function post(baseUrl: string, path: string, body: unknown, hint: string) {
  const response = await fetch(`${baseUrl}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Local ${path} failed (${response.status}). ${hint} ${detail}`.trim());
  }

  return response.json();
}

function session(token: string, propertyId: unknown, groupId: unknown): DevSession {
  const headers: RequestHeaders = { Authorization: `Bearer ${token}` };

  return {
    propertyId: (propertyId as string) ?? null,
    groupId: (groupId as string) ?? null,
    getHeaders: () => Promise.resolve(headers),
  };
}

export async function resolveDevSession(baseUrl: string): Promise<DevSession | null> {
  if (!env.DEV) {
    return null;
  }

  const email = env.VITE_CHATOBI_DEV_EMAIL;
  const password = env.VITE_CHATOBI_DEV_PASSWORD;
  const propertyID = env.VITE_CHATOBI_DEV_PROPERTY_ID ?? '';
  const groupID = env.VITE_CHATOBI_DEV_GROUP_ID ?? '';

  if ((email || password) && !(email && password)) {
    throw new Error(
      'Set both VITE_CHATOBI_DEV_EMAIL and VITE_CHATOBI_DEV_PASSWORD, or neither, in evo/.env.',
    );
  }

  if (email && password && (propertyID || groupID)) {
    const data = await post(
      baseUrl,
      'internalToken',
      { email, password, propertyID, groupID },
      'Check the VITE_CHATOBI_DEV_EMAIL / _PASSWORD / _PROPERTY_ID values.',
    );

    return session(data.token, data.propertyID ?? propertyID, data.groupID ?? groupID);
  }

  const code = env.VITE_CHATOBI_DEV_CODE;

  if (!code) {
    // Without credentials the API calls will fail, but the configured property
    // still lets the screens render against the harness' OIDC token.
    return propertyID || groupID
      ? { propertyId: propertyID || null, groupId: groupID || null, getHeaders: null }
      : null;
  }

  const data = await post(
    baseUrl,
    'verification',
    { random_string: code },
    'Launch codes are single-use and expire — take a fresh one from a /verify/<code> link.',
  );

  return session(data.auth_token, data.propertyID, data.groupID);
}
